import { httpSingleton } from '@/lib/http-client';
import {
	DOCUMENT_UPLOAD_STATUS_ID,
	DocumentModel,
	FileRestrictionsModel,
	FormOption,
	PaginatedResponseModel,
	PresignedUploadModel,
	PresignedUploadRequestModel,
} from '@/lib/models';
import { constructUrl } from '@/lib/utils';

interface CreateOrUpdateBlogRequestBody {
	title: string;
	content: string;
	imageRemoved: boolean;
	allowPublicView: boolean;
	programTags: {
		programId: string;
		topicTagIds: string[];
	}[];
}

export const blogsService = {
	getPublicBlogFilters() {
		return httpSingleton.orchestrateRequest<{ topics: FormOption[] }>({
			method: 'GET',
			url: '/advisory-service/api/v1/public/blogs/filters',
		});
	},
	getPublicBlogs(queryParameters?: { page?: number; size?: number; topicIds?: string[] }) {
		return httpSingleton.orchestrateRequest<PaginatedResponseModel & { results: DocumentModel[] }>({
			method: 'GET',
			url: constructUrl('/advisory-service/api/v1/public/blogs', queryParameters),
		});
	},
	getPublicBlog(blogId: string) {
		return httpSingleton.orchestrateRequest<{ blog: DocumentModel }>({
			method: 'GET',
			url: `/advisory-service/api/v1/public/blog/${blogId}`,
		});
	},
	getFilters() {
		return httpSingleton.orchestrateRequest<{ programs: FormOption[] }>({
			method: 'GET',
			url: '/advisory-service/api/v1/blog/filters',
		});
	},
	getMyBlogsFilters() {
		return httpSingleton.orchestrateRequest<{ programs: FormOption[] }>({
			method: 'GET',
			url: '/advisory-service/api/v1/blog/my-blogs/filters',
		});
	},
	getBlogs(queryParameters?: { page?: string; size?: string; programIds?: string[] }) {
		return httpSingleton.orchestrateRequest<PaginatedResponseModel & { results: DocumentModel[] }>({
			method: 'GET',
			url: constructUrl('/advisory-service/api/v1/blogs/authored', queryParameters),
		});
	},
	getBlog(blogId: string) {
		return httpSingleton.orchestrateRequest<{ blog: DocumentModel }>({
			method: 'GET',
			url: `/advisory-service/api/v1/blog/${blogId}`,
		});
	},

	getFormOptions() {
		return httpSingleton.orchestrateRequest<{
			fileRestrictions: FileRestrictionsModel;
			programs: FormOption[];
		}>({
			method: 'GET',
			url: '/advisory-service/api/v1/blog/form-options',
		});
	},
	createBlog(data: CreateOrUpdateBlogRequestBody) {
		return httpSingleton.orchestrateRequest<{ blog: DocumentModel }>({
			method: 'POST',
			url: '/advisory-service/api/v1/blog',
			data,
		});
	},
	updateBlog(blogId: string, data: CreateOrUpdateBlogRequestBody) {
		return httpSingleton.orchestrateRequest<{ blog: DocumentModel }>({
			method: 'PUT',
			url: `/advisory-service/api/v1/blog/${blogId}`,
			data,
		});
	},
	getPresignedUpload(blogId: string, data: PresignedUploadRequestModel) {
		return httpSingleton
			.orchestrateRequest<{ upload: PresignedUploadModel }>({
				method: 'POST',
				url: `/advisory-service/api/v1/blog/${blogId}/image-upload`,
				data,
			})
			.fetch();
	},
	updateAssetStatus(blogId: string, assetId: string, data: { uploadStatusId: DOCUMENT_UPLOAD_STATUS_ID }) {
		return httpSingleton
			.orchestrateRequest<void>({
				method: 'PUT',
				url: `/advisory-service/api/v1/blog/${blogId}/image-upload/${assetId}`,
				data,
			})
			.fetch();
	},
	rateBlog(blogId: string, data: { rating: number }) {
		return httpSingleton.orchestrateRequest<{ blog: DocumentModel }>({
			method: 'POST',
			url: `/advisory-service/api/v1/blog/${blogId}/rate`,
			data,
		});
	},
	followAuthor(data: { authorId: string; follow: boolean }) {
		return httpSingleton.orchestrateRequest<void>({
			method: 'POST',
			url: `/advisory-service/api/v1/blog/follow`,
			data,
		});
	},
	trackPrintActivity(blogId: string) {
		return httpSingleton.orchestrateRequest<void>({
			method: 'GET',
			url: `/advisory-service/api/v1/blog/${blogId}/print-activity`,
		});
	},
};
